import { apiClient, HttpError } from '@littlebird/auth-client';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import { queryClient } from '@/query/client';

import {
  invalidatePeople,
  invalidatePeopleList,
  personDetailSchema,
  personQueryOptions,
  personWriteScope,
  toWikiPersonDetail,
  useOpenPerson,
} from '../people-wiki.query';
import {
  WIKI_PROVIDERS,
  type WikiFactKind,
  type WikiPerson,
  type WikiPersonDetail,
  type WikiProvider,
} from '../people-wiki.types';

export const useSaveUserNotesMutation = (personId: string) =>
  useMutation(
    {
      mutationFn: async (userNotes: string): Promise<WikiPersonDetail> => {
        const response = await apiClient.patch(
          `/people-wiki/people/${personId}`,
          { user_notes: userNotes }
        );
        return toWikiPersonDetail(personDetailSchema.parse(response));
      },
      // One save at a time per person: two quick edit-and-blur cycles would otherwise race, and the older
      // response could land last, on the server and in the cache.
      scope: personWriteScope(personId),
      // Cancel an in-flight GET so it cannot resolve after the PATCH and overwrite the saved notes with stale ones.
      onMutate: () =>
        queryClient.cancelQueries({
          queryKey: personQueryOptions(personId).queryKey,
        }),
      onSuccess: (person) =>
        queryClient.setQueryData(personQueryOptions(personId).queryKey, person),
      onError: () => toast.error("Couldn't save your notes. Try again."),
    },
    queryClient
  );

export const useSetTrustedMutation = (personId: string) =>
  useMutation(
    {
      mutationFn: (isTrusted: boolean) =>
        apiClient.patch(`/people-wiki/people/${personId}`, {
          is_trusted: isTrusted,
        }),
      scope: personWriteScope(personId),
      // Refetch so the row moves to its new place and an open person page agrees; pending lasts until then
      onSuccess: () => invalidatePeople(),
      onError: () => toast.error("Couldn't change trust. Try again."),
    },
    queryClient
  );

// Each attempt of a write that is not idempotent would repeat it on the server
const NO_RETRIES = { retryOptions: { retries: 0 } };

type NewAccount = { provider: WikiProvider; value: string };

const postPerson = async (
  path: string,
  body: object
): Promise<WikiPersonDetail> => {
  const response = await apiClient.post(path, body, NO_RETRIES);
  return toWikiPersonDetail(personDetailSchema.parse(response));
};

const conflictSchema = z.object({
  person_id: z.string(),
  name: z.string(),
  is_self: z.boolean().default(false),
});

/** The person that an account is on already. */
export type AccountConflict = {
  personId: string;
  name: string;
  /** The user's own page, which no other page can be merged into. */
  isSelf: boolean;
};

/** The other person, when the server refused to add an account because it is on them. */
export const accountConflict = (error: unknown): AccountConflict | null => {
  if (!(error instanceof HttpError) || error.status !== 409) return null;
  const conflict = conflictSchema.safeParse(error.body);
  return conflict.success
    ? {
        personId: conflict.data.person_id,
        name: conflict.data.name,
        isSelf: conflict.data.is_self,
      }
    : null;
};

/** The server's plain-language reason for a refused edit, when it gave one. */
const refusalReason = (error: unknown): string | null =>
  error instanceof HttpError &&
  error.status === 422 &&
  typeof error.body?.detail === 'string'
    ? error.body.detail
    : null;

const reportEditError = (error: unknown, fallback: string) => {
  // An account that is on another person is offered a merge where it was typed, not a toast
  if (accountConflict(error) === null) {
    toast.error(refusalReason(error) ?? fallback);
  }
};

/** Create a page named "New person" and open it with its name ready to be typed over. */
export const useCreatePersonMutation = () => {
  const openPerson = useOpenPerson();
  return useMutation(
    {
      mutationFn: () => postPerson('/people-wiki/people', {}),
      onSuccess: async (person) => {
        queryClient.setQueryData(
          personQueryOptions(person.id).queryKey,
          person
        );
        void invalidatePeopleList();
        await openPerson(person.id, { rename: true });
      },
      onError: () => toast.error("Couldn't add this person. Try again."),
    },
    queryClient
  );
};

const usePersonEditMutation = <TVariables>(
  personId: string,
  request: (variables: TVariables) => Promise<unknown>,
  failureMessage: string
) => {
  return useMutation(
    {
      mutationFn: async (variables: TVariables): Promise<WikiPersonDetail> =>
        toWikiPersonDetail(personDetailSchema.parse(await request(variables))),
      scope: personWriteScope(personId),
      // Cancel an in-flight GET so it cannot resolve after the write and replace the page with stale data.
      onMutate: () =>
        queryClient.cancelQueries({
          queryKey: personQueryOptions(personId).queryKey,
        }),
      onSuccess: (person) => {
        queryClient.setQueryData(personQueryOptions(personId).queryKey, person);
        void invalidatePeopleList();
      },
      onError: (error) => reportEditError(error, failureMessage),
    },
    queryClient
  );
};

const personUrl = (personId: string) => `/people-wiki/people/${personId}`;

export const useEditPersonMutation = (personId: string) =>
  usePersonEditMutation(
    personId,
    (changes: {
      name?: string;
      add_aliases?: string[];
      remove_aliases?: string[];
    }) => apiClient.patch(personUrl(personId), changes),
    "Couldn't save your changes. Try again."
  );

export const useAddAccountMutation = (personId: string) =>
  usePersonEditMutation(
    personId,
    (account: NewAccount) =>
      apiClient.post(`${personUrl(personId)}/accounts`, account, NO_RETRIES),
    "Couldn't add this account. Try again."
  );

export const useRemoveAccountMutation = (personId: string) =>
  usePersonEditMutation(
    personId,
    (accountId: string) =>
      apiClient.delete(`${personUrl(personId)}/accounts/${accountId}`),
    "Couldn't remove this account. Try again."
  );

export const useAddFactMutation = (personId: string) =>
  usePersonEditMutation(
    personId,
    (fact: { kind: WikiFactKind; text: string }) =>
      apiClient.post(`${personUrl(personId)}/facts`, fact, NO_RETRIES),
    "Couldn't add this fact. Try again."
  );

export const useRemoveFactMutation = (personId: string) =>
  usePersonEditMutation(
    personId,
    (factId: string) =>
      apiClient.delete(`${personUrl(personId)}/facts/${factId}`),
    "Couldn't remove this fact. Try again."
  );

// An email that an app saw is on the page as that app's account, and adding it by hand would list it twice
const hasEmail = (person: WikiPerson, { provider, value }: NewAccount) =>
  provider === WIKI_PROVIDERS.email &&
  [person.primaryEmail, ...person.otherEmails].some(
    (email) => email?.toLowerCase() === value.toLowerCase()
  );

/** Merge the people, in the order given, and open the page that remains. The first is the one that remains, unless
 * the user's own page is among them. With an `account`, it is then added to the page that remains. */
export const useMergePeopleMutation = (personId: string) => {
  const openPerson = useOpenPerson();
  return useMutation(
    {
      mutationFn: async ({
        personIds,
        account,
      }: {
        personIds: string[];
        account?: NewAccount;
      }): Promise<WikiPersonDetail> => {
        const merged = await postPerson('/people-wiki/people/merge', {
          person_ids: personIds,
        });
        if (account === undefined || hasEmail(merged, account)) return merged;
        // Adding an account that came along with the merge changes nothing
        try {
          return await postPerson(`${personUrl(merged.id)}/accounts`, account);
        } catch (error) {
          toast.error(
            refusalReason(error) ??
              "Merged, but couldn't add this account. Try again."
          );
          return merged;
        }
      },
      scope: personWriteScope(personId),
      onSuccess: async (person, { personIds }) => {
        queryClient.setQueryData(
          personQueryOptions(person.id).queryKey,
          person
        );
        // Leave the page before the lists refetch, because a page that no longer exists would answer 404.
        await openPerson(person.id);
        for (const mergedId of personIds) {
          if (mergedId !== person.id) {
            queryClient.removeQueries({
              queryKey: personQueryOptions(mergedId).queryKey,
              exact: true,
            });
          }
        }
        void invalidatePeopleList();
        toast.success(`Merged into ${person.name}`);
      },
      onError: (error) =>
        reportEditError(error, "Couldn't merge these people. Try again."),
    },
    queryClient
  );
};
